import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import Cards from "react-credit-cards";
import classNames from "classnames";
import * as yup from "yup";
import "react-credit-cards/es/styles-compiled.css";
import "@/assets/styles/components/_payment-method.styles.scss";
import {
  PaymentMethodProps,
  PaymentRequest,
  BinCheckRequest,
  BinCheckResponse,
  PaymentResponse,
} from "./paymentMethod.types.ts";
import Info from "@/assets/svg/info.svg";
import { useMutation } from "react-query";
import { binCheck } from "@/utility";
import { PaymentModal } from "@/screens/dashboard/_components/PaymentModal/PaymentModal.tsx";

const creditCardSchema = yup.object().shape({
  name: yup
    .string()
    .required("Kart sahibinin adı zorunludur")
    .min(2, "Kart sahibinin adı en az 2 karakter olmalıdır")
    .matches(
      /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s]+$/,
      "Kart sahibinin adı sadece harf içerebilir",
    ),
  number: yup
    .string()
    .required("Kart numarası zorunludur")
    .min(16, "Kart numarası 16 haneli olmalıdır")
    .max(16, "Kart numarası 16 haneli olmalıdır"),
  expiry: yup
    .string()
    .required("Son kullanma tarihi zorunludur")
    .matches(
      /^(0[1-9]|1[0-2])\/\d{2}$/,
      "Geçerli bir son kullanma tarihi giriniz",
    ),
  cvc: yup
    .string()
    .required("CVC zorunludur")
    .min(3, "CVC en az 3 haneli olmalıdır")
    .max(4, "CVC en fazla 4 haneli olmalıdır")
    .matches(/^\d+$/, "CVC sadece rakam içerebilir"),
  preInfoForm: yup
    .boolean()
    .oneOf([true], "Ön bilgilendirme formu kabul edilmelidir"),
  distanceSalesContract: yup
    .boolean()
    .oneOf([true], "Mesafeli satış sözleşmesi kabul edilmelidir"),
});

const processPayment = async (
  paymentData: PaymentRequest,
): Promise<PaymentResponse> => {
  const response = await fetch(process.env.PAYMENT_API!, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(paymentData),
  });

  if (!response.ok) {
    throw new Error("Ödeme işlemi başarısız: " + response.status);
  }

  return response.json();
};

export const PaymentMethod = forwardRef<any, PaymentMethodProps>(
  ({ selectedPackage, onPaymentSuccess, uuid }, ref) => {
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [paymentModalState, setPaymentModalState] = useState<{
      isOpen: boolean;
      type: "success" | "iframe" | "error";
      paymentUrl?: string;
      errorMessage?: string;
    }>({
      isOpen: false,
      type: "success",
    });

    const { mutate, data, reset } = useMutation<
      BinCheckResponse,
      Error,
      BinCheckRequest
    >({
      mutationFn: (request: BinCheckRequest) => binCheck(request),
    });

    const paymentMutation = useMutation<PaymentResponse, Error, PaymentRequest>(
      {
        mutationFn: processPayment,
        onSuccess: (responseData: PaymentResponse) => {
          if (responseData?.dpPay && responseData?.paymentUrl) {
            setPaymentModalState({
              isOpen: true,
              type: "iframe",
              paymentUrl: responseData.paymentUrl,
            });
          } else {
            setPaymentModalState({
              isOpen: true,
              type: "error",
              errorMessage: "Ödeme işlemi başarısız oldu",
            });
          }
        },
        onError: (error: Error) => {
          setPaymentModalState({
            isOpen: true,
            type: "error",
            errorMessage:
              "Ödeme işlemi sırasında hata oluştu: " + error.message,
          });
        },
        onSettled: () => {
          setIsProcessingPayment(false);
        },
      },
    );

    const [cardForm, setCardForm] = useState({
      number: "",
      name: "",
      expiry: "",
      cvc: "",
      focused: "",
    });
    const [checkboxes, setCheckboxes] = useState({
      preInfoForm: false,
      distanceSalesContract: false,
    });
    const [selectedInstallment, setSelectedInstallment] = useState<
      number | null
    >(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useImperativeHandle(ref, () => ({
      resetForm: () => {
        setCardForm({
          number: "",
          name: "",
          expiry: "",
          cvc: "",
          focused: "",
        });
        setCheckboxes({
          preInfoForm: false,
          distanceSalesContract: false,
        });
        setSelectedInstallment(null);
        setErrors({});
        setPaymentModalState({
          isOpen: false,
          type: "success",
        });
        reset();
      },
    }));

    const formatCardNumber = (value: string) => {
      const numbers = value.replace(/\D/g, "");
      const formatted = numbers.replace(/(\d{4})(?=\d)/g, "$1 ");
      return formatted;
    };

    const getCleanCardNumber = (value: string) => {
      return value.replace(/\D/g, "");
    };

    const handleInstallmentSelect = (installmentCount: number) => {
      setSelectedInstallment(installmentCount);
    };

    const updateCardNumber = (value: string) => {
      const cleanValue = getCleanCardNumber(value);

      if (cleanValue.length <= 16) {
        setCardForm((prev) => ({
          ...prev,
          number: cleanValue,
        }));
      }
    };

    const handleCardInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      if (name === "number") {
        updateCardNumber(value);
      } else if (name === "name") {
        const cleanValue = value.replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, "");
        setCardForm((prev) => ({
          ...prev,
          [name]: cleanValue,
        }));
      } else if (name === "cvc") {
        const cleanValue = value.replace(/\D/g, "");
        if (cleanValue.length <= 4) {
          setCardForm((prev) => ({
            ...prev,
            [name]: cleanValue,
          }));
        }
      } else {
        setCardForm((prev) => ({
          ...prev,
          [name]: value,
        }));
      }

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    };

    const handleCardInputEvent = (e: React.FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const { name, value } = target;

      if (name === "number") {
        updateCardNumber(value);
      }
    };

    const handleCardInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setCardForm((prev) => ({
        ...prev,
        focused: e.target.name,
      }));
    };

    const handleExpiryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      const currentExpiry = cardForm.expiry;

      if (name === "expiry-month") {
        const year = currentExpiry.split("/")[1] || "";
        const newExpiry = `${value}/${year}`;
        setCardForm((prev) => ({
          ...prev,
          expiry: newExpiry,
        }));
      } else if (name === "expiry-year") {
        const month = currentExpiry.split("/")[0] || "";
        const newExpiry = `${month}/${value.slice(-2)}`;
        setCardForm((prev) => ({
          ...prev,
          expiry: newExpiry,
        }));
      }

      if (errors.expiry) {
        setErrors((prev) => ({
          ...prev,
          expiry: "",
        }));
      }
    };

    const handleCheckboxChange = (
      name: "preInfoForm" | "distanceSalesContract",
    ) => {
      setCheckboxes((prev) => ({
        ...prev,
        [name]: !prev[name],
      }));

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    };

    const validateForm = async () => {
      try {
        const formData = {
          ...cardForm,
          ...checkboxes,
        };
        await creditCardSchema.validate(formData, { abortEarly: false });
        setErrors({});
        return true;
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          const validationErrors: Record<string, string> = {};
          err.inner.forEach((error) => {
            if (error.path) {
              validationErrors[error.path] = error.message;
            }
          });
          setErrors(validationErrors);
        }
        return false;
      }
    };

    const handlePaymentComplete = async () => {
      // Form validasyonu (paket ve taksit kontrolü dahil)
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      setIsProcessingPayment(true);

      const [expiryMonth, expiryYear] = cardForm.expiry.split("/");

      const paymentData: PaymentRequest = {
        operation: "cc",
        product_ids: selectedPackage,
        c_family: data?.cardFamilyName || "",
        c_bank_name: data?.bank_name || "",
        c_bank_code: data?.bank_code || "",
        cvc: cardForm.cvc,
        expiryMonth: expiryMonth,
        expiryYear: parseInt(`20${expiryYear}`),
        installment: selectedInstallment,
        name: cardForm.name.toUpperCase(),
        number: cardForm.number,
        userId: String(uuid),
      };

      paymentMutation.mutate(paymentData);
    };

    const getInstallmentOptions = () => {
      if (data?.installments && data.installments.length > 0) {
        return data.installments?.map((installment) => ({
          count: installment?.installmentNumber,
          monthlyAmount: installment?.installmentPrice,
          totalAmount: installment?.totalPrice,
        }));
      }
      return [];
    };

    const isFormComplete = () => {
      const isCardFormComplete =
        cardForm.name.trim() !== "" &&
        cardForm.number.length === 16 &&
        cardForm.expiry.split("/").length === 2 &&
        cardForm.expiry.split("/")[0] !== "" &&
        cardForm.expiry.split("/")[1] !== "" &&
        cardForm.cvc.length >= 3 &&
        checkboxes.preInfoForm &&
        checkboxes.distanceSalesContract;

      const isBinCheckRequired = cardForm.number.length >= 8;

      const isBinCheckComplete = isBinCheckRequired
        ? data?.installments && data.installments.length > 0
        : true;

      const isInstallmentSelected = selectedInstallment !== null;

      return isCardFormComplete && isBinCheckComplete && isInstallmentSelected;
    };

    const closePaymentModal = () => {
      setPaymentModalState({
        isOpen: false,
        type: "success",
      });
    };

    const handleIframeMessage = (event: MessageEvent) => {
      if (event?.data?.dpPay === true) {
        if (event?.data?.resetRequired === true) {
          if (onPaymentSuccess) {
            onPaymentSuccess();
          }
        }
      }
    };

    useEffect(() => {
      if (data?.installments && data.installments.length > 0) {
        if (
          data.installments.length === 1 &&
          data.installments[0].installmentNumber === 1
        ) {
          setSelectedInstallment(1);
        } else {
          setSelectedInstallment(null);
        }
      }
    }, [data]);

    useEffect(() => {
      if (cardForm?.number?.length >= 8 && selectedPackage && uuid) {
        const binCode = cardForm.number.substring(0, 8);
        mutate({
          bincode: binCode,
          operation: "binCheck",
          packageIds: selectedPackage,
          userId: String(uuid),
        });
      } else if (cardForm?.number?.length < 8) {
        setSelectedInstallment(null);
        reset();
      }
    }, [cardForm?.number, mutate, reset, selectedPackage, uuid]);

    return (
      <div className="section-content-payment-method">
        <div className="section-intro">
          <h3 className="content-title">Ödeme Yöntemi</h3>
          <p className="content-description">
            Satın alma işlemini tamamlamak için lütfen formu doldurun.
          </p>
        </div>

        <div className="payment-methods">
          <div className="payment-form">
            <div className="payment-form-container">
              <div className="form-fields">
                <div className="form-section">
                  <h4 className="form-title">Kart Sahibinin Adı</h4>
                  <input
                    type="text"
                    name="name"
                    placeholder="Kart Üzerindeki Ad Soyad"
                    className={classNames("form-input", {
                      error: errors.name,
                    })}
                    value={cardForm.name}
                    onChange={handleCardInputChange}
                    onFocus={handleCardInputFocus}
                  />
                  <span className="error-message">{errors.name || ""}</span>
                </div>

                <div className="form-section">
                  <h4 className="form-title">Kart Numarası</h4>
                  <input
                    type="text"
                    name="number"
                    placeholder="**** **** **** ****"
                    className={classNames("form-input", {
                      error: errors.number,
                    })}
                    value={formatCardNumber(cardForm.number)}
                    onChange={handleCardInputChange}
                    onInput={handleCardInputEvent}
                    onFocus={handleCardInputFocus}
                    maxLength={19}
                  />
                  <span className="error-message">{errors.number || ""}</span>
                </div>

                <div className="form-row">
                  <div className="form-section">
                    <h4 className="form-title">Son Kullanma Tarihi</h4>
                    <div className="expiry-inputs">
                      <select
                        className={classNames("form-select", {
                          error: errors.expiry,
                        })}
                        name="expiry-month"
                        onChange={handleExpiryChange}
                        onFocus={() =>
                          setCardForm((prev) => ({
                            ...prev,
                            focused: "expiry",
                          }))
                        }
                        value={cardForm.expiry.split("/")[0] || ""}
                      >
                        <option value="">Ay</option>
                        {Array.from({ length: 12 }, (_, i) => {
                          const monthValue = String(i + 1).padStart(2, "0");
                          return (
                            <option key={i + 1} value={monthValue}>
                              {monthValue}
                            </option>
                          );
                        })}
                      </select>
                      <select
                        className={classNames("form-select", {
                          error: errors.expiry,
                        })}
                        name="expiry-year"
                        onChange={handleExpiryChange}
                        onFocus={() =>
                          setCardForm((prev) => ({
                            ...prev,
                            focused: "expiry",
                          }))
                        }
                        value={
                          cardForm.expiry.split("/")[1]
                            ? `20${cardForm.expiry.split("/")[1]}`
                            : ""
                        }
                      >
                        <option value="">Yıl</option>
                        {Array.from({ length: 10 }, (_, i) => {
                          const yearValue = String(2024 + i);
                          return (
                            <option key={2024 + i} value={yearValue}>
                              {yearValue}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <span className="error-message">{errors.expiry || ""}</span>
                  </div>

                  <div className="form-section">
                    <h4 className="form-title">CVC</h4>
                    <input
                      type="text"
                      name="cvc"
                      placeholder="CVC"
                      className={classNames("form-input", {
                        error: errors.cvc,
                      })}
                      value={cardForm.cvc}
                      onChange={handleCardInputChange}
                      onFocus={handleCardInputFocus}
                      maxLength={4}
                    />
                    <span className="error-message">{errors.cvc || ""}</span>
                  </div>
                </div>

                <div className="form-checkboxes">
                  <div
                    className={classNames("modern-checkbox", {
                      error: errors.preInfoForm,
                    })}
                  >
                    <input
                      type="checkbox"
                      id="preInfoForm"
                      checked={checkboxes.preInfoForm}
                      onChange={() => handleCheckboxChange("preInfoForm")}
                    />
                    <label htmlFor="preInfoForm">
                      <span className="checkbox-text">
                        <span className="checkbox-link">
                          Ön Bilgilendirme Formu
                        </span>
                        'nu okudum.
                      </span>
                    </label>
                  </div>

                  <div
                    className={classNames("modern-checkbox", {
                      error: errors.distanceSalesContract,
                    })}
                  >
                    <input
                      type="checkbox"
                      id="distanceSalesContract"
                      checked={checkboxes.distanceSalesContract}
                      onChange={() =>
                        handleCheckboxChange("distanceSalesContract")
                      }
                    />
                    <label htmlFor="distanceSalesContract">
                      <span className="checkbox-text">
                        <span className="checkbox-link">
                          Mesafeli Satış Sözleşmesi
                        </span>
                        'ni kabul ediyorum
                      </span>
                    </label>
                  </div>
                </div>

                <button
                  className={classNames("payment-button", {
                    "form-complete": isFormComplete(),
                    processing: isProcessingPayment,
                  })}
                  onClick={handlePaymentComplete}
                  disabled={isProcessingPayment} // ← Sadece processing sırasında disable
                >
                  {isProcessingPayment
                    ? "İşlem Gerçekleştiriliyor..."
                    : "Ödemeyi Tamamla"}
                </button>
              </div>

              <div className="credit-card-display">
                <Cards
                  cvc={cardForm.cvc}
                  expiry={cardForm.expiry}
                  focused={cardForm.focused as any}
                  name={cardForm.name}
                  number={cardForm.number}
                  locale={{ valid: "" }}
                />
              </div>
            </div>
            {getInstallmentOptions().length === 0 && (
              <div className="validity-info">
                <img src={Info} alt={"Info Icon"} />
                <span className="validity-text">
                  Kredi kartınıza ait taksit seçenekleri kart bilgilerinizi
                  girdikten sonra gösterilecektir.
                </span>
              </div>
            )}
            {getInstallmentOptions().length > 0 && (
              <div className="installment-options">
                <table className="installment-table">
                  <thead className="table-header">
                    <tr>
                      <th>Taksit Sayısı</th>
                      <th>Taksit Tutarı</th>
                      <th>Toplam Tutar</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {getInstallmentOptions().map((option) => (
                      <tr key={option.count}>
                        <td>
                          <label className="installment-option">
                            <input
                              type="radio"
                              name="installment"
                              value={option.count}
                              checked={selectedInstallment === option.count}
                              onChange={() =>
                                handleInstallmentSelect(option.count)
                              }
                              className="installment-radio"
                            />
                            <span className="installment-text">
                              {option.count === 1
                                ? "Tek Çekim"
                                : `${option.count} Taksit`}
                            </span>
                          </label>
                        </td>
                        <td>{option.monthlyAmount.toFixed(2)} TL</td>
                        <td>{option.totalAmount.toFixed(2)} TL</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <PaymentModal
          isOpen={paymentModalState.isOpen}
          onClose={closePaymentModal}
          type={paymentModalState.type}
          paymentUrl={paymentModalState.paymentUrl}
          errorMessage={paymentModalState.errorMessage}
          selectedPackage={selectedPackage}
          onIframeMessage={handleIframeMessage}
        />
      </div>
    );
  },
);
